/**
 * Staging style packs.
 *
 * ONE prop kit, three tints. The decision behind that shape is the user's own
 * observation that open houses all show the same things — a plant, books,
 * minimal decoration — which the staging literature agrees with: neutrals
 * dominate and colour enters only at the ~10% accent layer. So a pack does not
 * change WHICH props are placed, only their palette, their art motif and the
 * tone of their materials. Three prop vocabularies would be three times the
 * assets for a difference buyers do not read.
 *
 * KITCHENS are the exception, and a deliberate one: a pack also picks what a
 * kitchen prop IS — lemons, apples or pears in the bowl; an oak, stone or
 * walnut tray; turned-wood or acrylic mills; penne, spaghetti or fusilli in
 * the jar. Megan chose these per style over 11 review rounds (2026-10-18). The
 * choice lives in each pack's `kitchen` block; the planner stamps the style id
 * on a kitchen prop as `attributes.variant` and the renderer looks the block
 * up here, so planner and drawing read one table.
 *
 * Shape and discipline mirror `kitchenStylePresets` / `dormThemePresets`: a
 * plain table of pure data, resolved through a deep clone so a caller can
 * never mutate the shared record, and an unknown id throws rather than
 * silently falling back.
 */

const STAGING_STYLES = {
  // The safest staging default: "neutral plus warmth", and the style the
  // research names as the middle ground for resale.
  'warm-neutral': {
    id: 'warm-neutral',
    label: 'Warm neutral',
    palette: ['#EFE7D6', '#C2A98A', '#B98A4E', '#6C7A5C', '#7E7466'],
    accent: '#B98A4E',
    art: {
      motif: 'minimal-arch',
      colors: ['#EFE7D6', '#B98A4E', '#6C7A5C'],
      seed: 7,
    },
    towel: '#F3EEE2',
    kitchen: {
      // Kitchen art: a kitchen SUBJECT in this pack's own art palette, so the
      // house reads as one set by colour and frame (Megan's pick, 2026-10-21).
      art: {
        motif: 'kitchen-citrus-branch',
        colors: ['#EFE7D6', '#B98A4E', '#6C7A5C'],
        seed: 7,
      },
      fruit: 'lemon',
      vase: '#E4DACA',
      flower: '#F7F2E8',
      trayKind: 'rect',
      tray: '#C49A64',
      mill: 'turned',
      millWood: '#5A3A22',
      pasta: 'penne',
      crock: '#EDE6D8',
      canLid: '#B98A4E',
      cookbookCover: '#6C7A5C',
      teaTowel: '#B98A4E',
      teaTowelStripe: '#F3EEE2',
    },
  },
  'modern-minimal': {
    id: 'modern-minimal',
    label: 'Modern minimal',
    palette: ['#F4F4F2', '#DDDDD8', '#9A9A95', '#4A4A47', '#2B2B29'],
    accent: '#9A9A95',
    art: {
      motif: 'neutral-color-block',
      colors: ['#F4F4F2', '#9A9A95', '#2B2B29'],
      seed: 11,
    },
    towel: '#FBFBFA',
    kitchen: {
      // Kitchen art: a kitchen SUBJECT in this pack's own art palette, so the
      // house reads as one set by colour and frame (Megan's pick, 2026-10-21).
      art: {
        motif: 'kitchen-citrus-slice',
        colors: ['#F4F4F2', '#9A9A95', '#2B2B29'],
        seed: 11,
      },
      fruit: 'apple',
      vase: '#3A3A37',
      flower: '#FAFAF7',
      trayKind: 'round',
      tray: '#3B3B38',
      mill: 'acrylic',
      millMetal: '#BFC2C4',
      pasta: 'spaghetti',
      crock: '#F4F4F2',
      canLid: '#2B2B29',
      cookbookCover: '#2B2B29',
      teaTowel: '#7C7C77',
      teaTowelStripe: '#FBFBFA',
    },
  },
  'warm-organic': {
    id: 'warm-organic',
    label: 'Warm organic',
    palette: ['#EDE4D3', '#CBB79A', '#A9754F', '#7E8B6B', '#5B5345'],
    accent: '#A9754F',
    art: {
      motif: 'botanical-plate',
      colors: ['#EDE4D3', '#A9754F', '#7E8B6B'],
      seed: 23,
    },
    towel: '#F2EADC',
    kitchen: {
      // Kitchen art: a kitchen SUBJECT in this pack's own art palette, so the
      // house reads as one set by colour and frame (Megan's pick, 2026-10-21).
      art: {
        motif: 'kitchen-herb-botanical',
        colors: ['#EDE4D3', '#A9754F', '#7E8B6B'],
        seed: 23,
      },
      fruit: 'pear',
      vase: '#CBB79A',
      flower: '#EFD9C6',
      trayKind: 'oval',
      tray: '#7A4E2E',
      mill: 'turned',
      millWood: '#A57B4F',
      pasta: 'fusilli',
      crock: '#D9CCB6',
      canLid: '#7E8B6B',
      cookbookCover: '#7E8B6B',
      teaTowel: '#A9754F',
      teaTowelStripe: '#F2EADC',
    },
  },
};

const STAGING_STYLE_ORDER = Object.keys(STAGING_STYLES);
const DEFAULT_STAGING_STYLE_ID = 'warm-neutral';

function resolveStagingStyle(styleId) {
  const id = styleId || DEFAULT_STAGING_STYLE_ID;
  const found = STAGING_STYLES[id];
  if (!found) {
    throw new Error(
      `resolveStagingStyle: unknown style "${id}". Known: ${STAGING_STYLE_ORDER.join(', ')}`
    );
  }
  // Deep clone so a planner can never write through to the shared table.
  return JSON.parse(JSON.stringify(found));
}

/**
 * A style's kitchen look, by style id. Renderers receive the id as
 * `options.variant`; an unknown or missing id draws the default style rather
 * than nothing, because a persisted prop must always render.
 */
function kitchenLookFor(styleId) {
  const pack =
    STAGING_STYLES[styleId] || STAGING_STYLES[DEFAULT_STAGING_STYLE_ID];
  return pack.kitchen;
}

module.exports = {
  kitchenLookFor,
  STAGING_STYLES,
  STAGING_STYLE_ORDER,
  DEFAULT_STAGING_STYLE_ID,
  resolveStagingStyle,
};
